import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { DollarSign, Loader2, Search, Download, Upload, FileSpreadsheet, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import PriorityBadge from "@/components/PriorityBadge";
import StatusBadge from "@/components/StatusBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDT = (iso) => {
  try {
    return format(new Date(iso), "MMM dd, yyyy HH:mm");
  } catch {
    return iso || "";
  }
};

const formatRM = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `RM ${num.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const monthKey = (iso) => {
  try {
    return format(new Date(iso), "MMM yyyy");
  } catch {
    return "";
  }
};

/**
 * BillingEditor — opens a Popover anchored to the row's amount cell / Billed chip
 * so finance can enter a price without leaving the Finance page.
 */
const BillingEditor = ({ row, canEdit, onSaved }) => {
  const billed = row.amount != null && Number(row.amount) > 0;
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(billed ? String(row.amount) : "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setVal(billed ? String(row.amount) : "");
  }, [open, billed, row.amount]);

  const save = async () => {
    const num = val === "" ? null : Number(val);
    if (val !== "" && (!Number.isFinite(num) || num < 0)) {
      toast.error("Enter a valid non-negative amount");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/work-orders/${row.id}`, { amount: num });
      toast.success(num ? `WO ${row.work_order_number} marked Billed (${formatRM(num)})` : `WO ${row.work_order_number} marked Not Billed`);
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update amount");
    } finally {
      setSaving(false);
    }
  };

  // Read-only chip + amount for roles that cannot edit
  if (!canEdit) {
    return (
      <>
        <span
          className={`font-mono tabular-nums text-sm font-semibold ${billed ? "text-[#09090B]" : "text-[#D97706]"}`}
          data-testid={`finance-amount-${row.work_order_number}`}
        >
          {billed ? formatRM(row.amount) : "—"}
        </span>
        {billed ? (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase bg-[#D1FAE5] text-[#047857] border border-[#A7F3D0]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#059669]" />Billed
          </span>
        ) : (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D97706]" />Not Billed
          </span>
        )}
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`group inline-flex items-center gap-2 rounded-sm px-2 py-1 -mx-2 -my-1 transition-colors ${
            billed
              ? "hover:bg-[#D1FAE5]"
              : "hover:bg-[#FEF3C7] focus:bg-[#FEF3C7]"
          } focus:outline-none focus:ring-2 focus:ring-[#0F172A]/20`}
          data-testid={`finance-billing-trigger-${row.work_order_number}`}
          title={billed ? "Click to update amount" : "Click to enter chargeable amount"}
        >
          <span
            className={`font-mono tabular-nums text-sm font-semibold ${billed ? "text-[#09090B]" : "text-[#D97706]"}`}
            data-testid={`finance-amount-${row.work_order_number}`}
          >
            {billed ? formatRM(row.amount) : "— set price"}
          </span>
          {billed ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase bg-[#D1FAE5] text-[#047857] border border-[#A7F3D0]"
              data-testid={`finance-billed-${row.work_order_number}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#059669]" />
              Billed
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]"
              data-testid={`finance-unbilled-${row.work_order_number}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#D97706]" />
              Not Billed
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] mb-1">
          {row.work_order_number} · {row.site_name}
        </div>
        <div className="text-xs text-[#71717A] mb-2 truncate" title={row.description}>
          {row.description}
        </div>
        <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">
          Amount (RM)
        </label>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          autoFocus
          className="mt-1 h-9 border-[#E4E4E7] rounded-sm font-mono"
          placeholder="0.00"
          data-testid={`finance-amount-input-${row.work_order_number}`}
        />
        <div className="flex items-center justify-between gap-2 mt-3">
          <button
            type="button"
            onClick={() => { setVal(""); }}
            className="text-xs text-[#71717A] hover:text-[#DC2626]"
            data-testid={`finance-amount-clear-${row.work_order_number}`}
          >
            Clear
          </button>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpen(false)}
              className="h-8 rounded-sm border-[#E4E4E7]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="h-8 bg-[#059669] hover:bg-[#047857] text-white rounded-sm"
              data-testid={`finance-amount-save-${row.work_order_number}`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>

        {/* Billing audit history (append-only, read-only) */}
        {Array.isArray(row.billing_history) && row.billing_history.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#E4E4E7]" data-testid={`finance-history-${row.work_order_number}`}>
            <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] mb-1.5">
              History ({row.billing_history.length})
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto text-[11px]">
              {[...row.billing_history].reverse().map((h, i) => {
                const old = h.old_amount == null ? "—" : `RM ${Number(h.old_amount).toFixed(2)}`;
                const neu = h.new_amount == null ? "—" : `RM ${Number(h.new_amount).toFixed(2)}`;
                const when = (() => {
                  try { return format(new Date(h.timestamp), "MMM d, HH:mm"); } catch { return h.timestamp; }
                })();
                return (
                  <div key={i} className="flex items-start justify-between gap-2 bg-[#F4F4F5] px-2 py-1 rounded-sm">
                    <div className="min-w-0">
                      <div className="font-mono tabular-nums text-[#09090B]">
                        {old} <span className="text-[#71717A]">→</span> <span className="font-semibold">{neu}</span>
                      </div>
                      <div className="text-[#71717A] text-[10px]">
                        @{h.user || "—"} · {when}{h.source === "import" ? " · import" : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const FinanceDialog = ({ open, onOpenChange }) => {
  const { user: currentUser } = useAuth();
  const canEditBilling = currentUser?.role === "finance" || currentUser?.role === "manager";
  const [tab, setTab] = useState("chargeable");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyFilterUser, setHistoryFilterUser] = useState("");
  const [historyFilterStart, setHistoryFilterStart] = useState("");
  const [historyFilterEnd, setHistoryFilterEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [importing, setImporting] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statementMonth, setStatementMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [statementStart, setStatementStart] = useState("");
  const [statementEnd, setStatementEnd] = useState("");
  const [statementLoading, setStatementLoading] = useState(false);

  // Statement date presets — returns [startISO, endISO] (yyyy-mm-dd)
  const presetRange = (preset) => {
    const now = new Date();
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (preset === "this_month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return [toISO(start), toISO(end)];
    }
    if (preset === "last_month") {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return [toISO(start), toISO(end)];
    }
    if (preset === "this_quarter") {
      const q = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), q * 3, 1);
      const end = new Date(now.getFullYear(), q * 3 + 3, 0);
      return [toISO(start), toISO(end)];
    }
    if (preset === "ytd") {
      const start = new Date(now.getFullYear(), 0, 1);
      return [toISO(start), toISO(now)];
    }
    return [toISO(now), toISO(now)];
  };
  const fileInputRef = useRef(null);

  const fetchChargeable = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work-orders`, {
        params: { is_chargeable: true },
      });
      setRows(res.data || []);
    } catch (e) {
      toast.error("Failed to load chargeable work orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchChargeable();
  }, [open]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = {};
      if (historyFilterUser.trim()) params.user = historyFilterUser.trim();
      if (historyFilterStart) params.start = historyFilterStart;
      if (historyFilterEnd) params.end = historyFilterEnd;
      const res = await axios.get(`${API}/finance/billing-history`, { params });
      setHistoryEntries(res.data.entries || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (open && tab === "history") fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        (r.work_order_number || "").toLowerCase().includes(needle) ||
        (r.site_name || "").toLowerCase().includes(needle) ||
        (r.description || "").toLowerCase().includes(needle) ||
        (r.engineer_name || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const statusBreakdown = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      const s = r.status || "Unknown";
      if (!acc[s]) acc[s] = { count: 0, revenue: 0 };
      acc[s].count += 1;
      acc[s].revenue += Number(r.amount) || 0;
    }
    // Sort by revenue desc so the biggest buckets come first
    return Object.entries(acc).sort((a, b) => b[1].revenue - a[1].revenue);
  }, [rows]);

  const totalRevenue = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
    [rows]
  );

  const filteredRevenue = useMemo(
    () => filtered.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
    [filtered]
  );

  const withoutAmountCount = useMemo(
    () => rows.filter((r) => r.amount == null).length,
    [rows]
  );

  const monthlyRevenue = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      if (r.amount == null) continue;
      const key = monthKey(r.start_datetime || r.created_at);
      if (!key) continue;
      acc[key] = (acc[key] || 0) + Number(r.amount || 0);
    }
    // Sort chronologically (parse "MMM yyyy" back to date)
    return Object.entries(acc)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .slice(-6); // last 6 months
  }, [rows]);

  const downloadXlsx = async (scope, extraParams = {}) => {
    try {
      const params = new URLSearchParams({ scope, ...extraParams });
      const res = await axios.get(`${API}/finance/export?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `work-orders-${scope}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const handleExportRange = async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Pick both start and end date");
      return;
    }
    await downloadXlsx("date_range", { start: dateFrom, end: dateTo });
  };

  const handleDownloadStatement = async (mode = "month", overrideRange = null) => {
    const params = {};
    let effectiveStart = statementStart;
    let effectiveEnd = statementEnd;
    if (mode === "range") {
      if (overrideRange) {
        [effectiveStart, effectiveEnd] = overrideRange;
      }
      if (!effectiveStart || !effectiveEnd) {
        toast.error("Pick both start and end dates");
        return;
      }
      if (effectiveEnd < effectiveStart) {
        toast.error("End date must be on or after start date");
        return;
      }
      params.start = effectiveStart;
      params.end = effectiveEnd;
    } else {
      if (!/^\d{4}-\d{2}$/.test(statementMonth)) {
        toast.error("Pick a valid month");
        return;
      }
      params.month = statementMonth;
    }
    setStatementLoading(true);
    try {
      const res = await axios.get(`${API}/finance/statement.pdf`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = mode === "range"
        ? `${effectiveStart}_to_${effectiveEnd}`
        : statementMonth;
      a.download = `billing-statement-${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Statement downloaded (${suffix})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate statement");
    } finally {
      setStatementLoading(false);
    }
  };

  const handlePreset = (preset) => {
    const [s, e] = presetRange(preset);
    setStatementStart(s);
    setStatementEnd(e);
    handleDownloadStatement("range", [s, e]);
  };

  const handleImportChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/finance/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      toast.success(`Imported: ${d.updated} updated, ${d.skipped} skipped`);
      if (d.errors && d.errors.length) {
        toast.warning(`${d.errors.length} row(s) had issues — see console`);
        console.warn("Finance import warnings:", d.errors);
      }
      fetchChargeable();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const exportCsv = () => {
    const header = ["WO Number", "Site", "Engineer", "Description", "Priority", "Status", "Amount (RM)", "Start", "End"];
    const lines = [header.join(",")];
    for (const r of filtered) {
      const row = [
        r.work_order_number,
        r.site_name,
        r.engineer_name || "",
        (r.description || "").replaceAll(",", ";"),
        r.priority,
        r.status,
        r.amount != null ? Number(r.amount).toFixed(2) : "",
        r.start_datetime,
        r.end_datetime,
      ];
      lines.push(row.map((v) => `"${(v ?? "").toString().replaceAll('"', '""')}"`).join(","));
    }
    // Totals footer
    lines.push("");
    lines.push(`"","","","","","TOTAL","${filteredRevenue.toFixed(2)}","",""`);
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chargeable-work-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none w-[98vw] h-[96vh] border-[#E4E4E7] rounded-sm p-0 flex flex-col overflow-hidden sm:rounded-sm"
        data-testid="finance-dialog"
      >
        <div className="flex-1 overflow-y-auto px-6 py-5">
        <DialogHeader className="border-b border-[#E4E4E7] pb-4">
          <DialogTitle
            className="text-2xl font-semibold tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Manrope" }}
          >
            <DollarSign className="w-6 h-6" />
            Finance — Chargeable Work Orders
          </DialogTitle>
          <DialogDescription className="text-sm text-[#71717A]">
            Read-only view of every work order marked as chargeable across the business.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-3">
          <TabsList data-testid="finance-tabs">
            <TabsTrigger value="chargeable" data-testid="finance-tab-chargeable">
              Chargeable WOs
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="finance-tab-history">
              History
            </TabsTrigger>
          </TabsList>
          <TabsContent value="chargeable">

        {/* Summary + controls */}
        <div className="flex flex-wrap gap-3 items-center py-3 border-b border-[#E4E4E7]">
          <div
            className="inline-flex items-baseline gap-2 px-3 py-1.5 bg-[#0F172A] text-white rounded-sm"
            data-testid="finance-total"
          >
            <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#94A3B8]">Total</span>
            <span className="text-lg font-bold">{rows.length}</span>
          </div>
          <div
            className="inline-flex items-baseline gap-2 px-3 py-1.5 bg-[#059669] text-white rounded-sm"
            data-testid="finance-revenue"
          >
            <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#A7F3D0]">Revenue</span>
            <span className="text-lg font-bold">{formatRM(totalRevenue)}</span>
          </div>
          {withoutAmountCount > 0 && (
            <div
              className="inline-flex items-baseline gap-2 px-3 py-1.5 bg-[#FEF3C7] text-[#D97706] rounded-sm border border-[#FDE68A]"
              title="Chargeable work orders still awaiting finance to enter an amount"
              data-testid="finance-missing-amount"
            >
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Not Billed</span>
              <span className="text-sm font-bold">{withoutAmountCount}</span>
            </div>
          )}
          {statusBreakdown.map(([s, agg]) => (
            <div
              key={s}
              className="inline-flex flex-col px-3 py-1.5 bg-[#F4F4F5] rounded-sm border border-[#E4E4E7] min-w-[110px]"
              data-testid={`finance-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
              title={`${agg.count} ${s} work order${agg.count > 1 ? "s" : ""} — ${formatRM(agg.revenue)}`}
            >
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">
                {s} · {agg.count}
              </span>
              <span className="text-sm font-bold text-[#09090B]">
                {formatRM(agg.revenue)}
              </span>
            </div>
          ))}
          <div className="flex-1" />
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#71717A] w-4 h-4" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search WO, site, engineer..."
              className="pl-10 border-[#E4E4E7] rounded-sm h-9"
              data-testid="finance-search"
            />
          </div>
          {/* Monthly / Date-range Statement — one-click PDF for finance */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={statementLoading}
                className="rounded-sm border-[#E4E4E7]"
                data-testid="finance-statement-btn"
                title="Download a billing statement PDF (monthly or custom range)"
              >
                {statementLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Statement PDF
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs tracking-[0.15em] uppercase text-[#71717A]">
                Quick presets
              </DropdownMenuLabel>
              <div className="px-2 py-1.5 grid grid-cols-2 gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePreset("this_month")}
                  disabled={statementLoading}
                  className="h-7 rounded-sm border-[#E4E4E7] text-[11px]"
                  data-testid="finance-preset-this-month"
                >
                  This month
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePreset("last_month")}
                  disabled={statementLoading}
                  className="h-7 rounded-sm border-[#E4E4E7] text-[11px]"
                  data-testid="finance-preset-last-month"
                >
                  Last month
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePreset("this_quarter")}
                  disabled={statementLoading}
                  className="h-7 rounded-sm border-[#E4E4E7] text-[11px]"
                  data-testid="finance-preset-this-quarter"
                >
                  This quarter
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePreset("ytd")}
                  disabled={statementLoading}
                  className="h-7 rounded-sm border-[#E4E4E7] text-[11px]"
                  data-testid="finance-preset-ytd"
                >
                  YTD
                </Button>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs tracking-[0.15em] uppercase text-[#71717A]">
                Monthly
              </DropdownMenuLabel>
              <div className="px-2 py-1.5 flex items-center gap-1.5">
                <Input
                  type="month"
                  value={statementMonth}
                  onChange={(e) => setStatementMonth(e.target.value)}
                  className="h-8 border-[#E4E4E7] rounded-sm text-xs flex-1"
                  data-testid="finance-statement-month"
                />
                <Button
                  size="sm"
                  onClick={() => handleDownloadStatement("month")}
                  className="h-8 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm text-xs"
                  data-testid="finance-statement-month-btn"
                >
                  PDF
                </Button>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs tracking-[0.15em] uppercase text-[#71717A]">
                Custom date range
              </DropdownMenuLabel>
              <div className="px-2 py-1.5 space-y-1.5">
                <Input
                  type="date"
                  value={statementStart}
                  onChange={(e) => setStatementStart(e.target.value)}
                  className="h-8 border-[#E4E4E7] rounded-sm text-xs"
                  placeholder="From"
                  data-testid="finance-statement-start"
                />
                <Input
                  type="date"
                  value={statementEnd}
                  onChange={(e) => setStatementEnd(e.target.value)}
                  className="h-8 border-[#E4E4E7] rounded-sm text-xs"
                  placeholder="To"
                  data-testid="finance-statement-end"
                />
                <Button
                  size="sm"
                  onClick={() => handleDownloadStatement("range")}
                  className="w-full h-8 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm text-xs"
                  data-testid="finance-statement-range-btn"
                >
                  PDF for date range
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="rounded-sm border-[#E4E4E7]"
            data-testid="finance-import-xlsx"
          >
            {importing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Import
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xlsm"
            onChange={handleImportChange}
            className="hidden"
            data-testid="finance-import-input"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-sm border-[#E4E4E7]"
                data-testid="finance-export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs tracking-[0.15em] uppercase text-[#71717A]">
                Excel export
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => downloadXlsx("chargeable")}
                data-testid="export-chargeable"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Chargeable only
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => downloadXlsx("all")}
                data-testid="export-all"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                All work orders
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs tracking-[0.15em] uppercase text-[#71717A]">
                Date range
              </DropdownMenuLabel>
              <div className="px-2 py-1.5 space-y-1.5">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-8 border-[#E4E4E7] rounded-sm text-xs"
                  placeholder="From"
                  data-testid="finance-date-from"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-8 border-[#E4E4E7] rounded-sm text-xs"
                  placeholder="To"
                  data-testid="finance-date-to"
                />
                <Button
                  size="sm"
                  onClick={handleExportRange}
                  className="w-full h-8 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm text-xs"
                  data-testid="export-date-range"
                >
                  Export range
                </Button>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={exportCsv}
                data-testid="export-csv-legacy"
                className="text-[#71717A] text-xs"
              >
                CSV (filtered view)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Monthly revenue strip */}
        {monthlyRevenue.length > 0 && (
          <div className="flex flex-wrap gap-2 py-2 border-b border-[#E4E4E7]" data-testid="finance-monthly-strip">
            <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] py-1">
              Last {monthlyRevenue.length} months
            </span>
            {monthlyRevenue.map(([m, amt]) => (
              <div
                key={m}
                className="inline-flex flex-col px-2.5 py-1 bg-[#F4F4F5] rounded-sm border border-[#E4E4E7] min-w-[92px]"
                data-testid={`finance-month-${m.replace(/\s+/g, "-")}`}
              >
                <span className="text-[10px] tracking-[0.1em] uppercase font-bold text-[#71717A]">{m}</span>
                <span className="text-xs font-bold text-[#09090B]">{formatRM(amt)}</span>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#71717A]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-[#71717A]" data-testid="finance-empty">
            No chargeable work orders yet.
          </div>
        ) : (
          <Table data-testid="finance-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">WO #</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Site</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Engineer</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Description</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Priority</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Status</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] text-right">Amount (RM)</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Billing</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Start</TableHead>
                <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">End</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const billed = r.amount != null && Number(r.amount) > 0;
                return (
                  <TableRow key={r.id} data-testid={`finance-row-${r.work_order_number}`}>
                    <TableCell className="font-semibold text-[#09090B]">{r.work_order_number}</TableCell>
                    <TableCell className="text-[#09090B]">{r.site_name}</TableCell>
                    <TableCell className="text-[#09090B]">
                      {r.engineer_name || <span className="text-[#71717A] italic">Unassigned</span>}
                    </TableCell>
                    <TableCell className="text-sm text-[#09090B] max-w-md truncate" title={r.description}>
                      {r.description}
                    </TableCell>
                    <TableCell><PriorityBadge priority={r.priority} /></TableCell>
                    <TableCell><StatusBadge status={r.status} isClosed={r.is_closed} /></TableCell>
                    <TableCell colSpan={2}>
                      <BillingEditor row={r} canEdit={canEditBilling} onSaved={fetchChargeable} />
                    </TableCell>
                    <TableCell className="text-xs text-[#71717A]">{formatDT(r.start_datetime)}</TableCell>
                    <TableCell className="text-xs text-[#71717A]">{formatDT(r.end_datetime)}</TableCell>
                  </TableRow>
                );
              })}
              {/* Totals row */}
              <TableRow className="bg-[#F4F4F5] font-bold">
                <TableCell colSpan={6} className="text-right text-xs tracking-[0.15em] uppercase text-[#71717A]">
                  Total (filtered)
                </TableCell>
                <TableCell
                  className="text-right font-mono tabular-nums text-sm font-bold text-[#09090B]"
                  data-testid="finance-total-amount"
                >
                  {formatRM(filteredRevenue)}
                </TableCell>
                <TableCell colSpan={3} />
              </TableRow>
            </TableBody>
          </Table>
        )}

        <div className="mt-2 text-xs text-[#71717A] border-t border-[#E4E4E7] pt-3">
          Showing {filtered.length} of {rows.length} chargeable work orders
        </div>
          </TabsContent>
          <TabsContent value="history">
            {/* History filters */}
            <div className="flex flex-wrap gap-2 items-end py-3 border-b border-[#E4E4E7]">
              <div>
                <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] block mb-1">
                  User
                </label>
                <Input
                  type="text"
                  value={historyFilterUser}
                  onChange={(e) => setHistoryFilterUser(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchHistory()}
                  placeholder="e.g. lakhbir"
                  className="h-9 w-48 border-[#E4E4E7] rounded-sm"
                  data-testid="history-filter-user"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] block mb-1">
                  From
                </label>
                <Input
                  type="date"
                  value={historyFilterStart}
                  onChange={(e) => setHistoryFilterStart(e.target.value)}
                  className="h-9 w-40 border-[#E4E4E7] rounded-sm"
                  data-testid="history-filter-start"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] block mb-1">
                  To
                </label>
                <Input
                  type="date"
                  value={historyFilterEnd}
                  onChange={(e) => setHistoryFilterEnd(e.target.value)}
                  className="h-9 w-40 border-[#E4E4E7] rounded-sm"
                  data-testid="history-filter-end"
                />
              </div>
              <Button
                onClick={fetchHistory}
                disabled={historyLoading}
                className="h-9 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm"
                data-testid="history-apply-btn"
              >
                {historyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </Button>
              {(historyFilterUser || historyFilterStart || historyFilterEnd) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setHistoryFilterUser("");
                    setHistoryFilterStart("");
                    setHistoryFilterEnd("");
                    setTimeout(fetchHistory, 0);
                  }}
                  className="h-9 rounded-sm border-[#E4E4E7]"
                  data-testid="history-clear-btn"
                >
                  Clear
                </Button>
              )}
              <div className="ml-auto text-xs text-[#71717A]" data-testid="history-count">
                {historyEntries.length} entries
              </div>
            </div>

            {/* History table */}
            {historyLoading ? (
              <div className="py-16 text-center text-[#71717A] text-sm">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                Loading history...
              </div>
            ) : historyEntries.length === 0 ? (
              <div className="py-16 text-center text-[#71717A] text-sm">
                No billing edits recorded yet for the selected filters.
              </div>
            ) : (
              <Table data-testid="history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">When</TableHead>
                    <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">User</TableHead>
                    <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">WO #</TableHead>
                    <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Site</TableHead>
                    <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] text-right">Old (RM)</TableHead>
                    <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] text-right">New (RM)</TableHead>
                    <TableHead className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A]">Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyEntries.map((e, i) => {
                    let when = e.timestamp;
                    try { when = format(new Date(e.timestamp), "MMM dd, yyyy HH:mm"); } catch (_) {}
                    const oldLabel = e.old_amount == null ? "—" : Number(e.old_amount).toFixed(2);
                    const newLabel = e.new_amount == null ? "—" : Number(e.new_amount).toFixed(2);
                    return (
                      <TableRow key={`${e.work_order_id}-${e.timestamp}-${i}`} data-testid={`history-row-${i}`}>
                        <TableCell className="text-xs text-[#71717A] font-mono">{when}</TableCell>
                        <TableCell className="text-xs">
                          <span className="font-semibold text-[#09090B]">@{e.user || "—"}</span>
                          <span className="text-[#71717A]"> · {e.role}</span>
                        </TableCell>
                        <TableCell className="font-semibold text-[#09090B]">{e.work_order_number}</TableCell>
                        <TableCell className="text-xs text-[#09090B]">{e.site_name}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm text-[#71717A]">
                          {oldLabel}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm font-semibold text-[#09090B]">
                          {newLabel}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase border ${
                              e.source === "import"
                                ? "bg-[#EDE9FE] text-[#6D28D9] border-[#DDD6FE]"
                                : "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]"
                            }`}
                          >
                            {e.source}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FinanceDialog;
